import React, {Fragment, useEffect, useState} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import API from '../../helpers/network/http';
import { useToast } from '../../hooks/useToast';
import {Song} from "../../utils/types";
import SongSearchInput from "../SongSearchInput";
import {COMMUNITY_CONTRIBUTION_PART_OPTIONS} from "../../globals/constants/communityParts";
import { useI18n } from '../../i18n';

const partTypes = COMMUNITY_CONTRIBUTION_PART_OPTIONS;

interface RequestContributionModalProps {
    show: boolean;
    onClose: () => void;
    song?: Song | null;
    song_id?: number | string;
    external_song_id?: string;
    default_part?: string;
}

const RequestContributionModal = ({
                                      show,
                                      onClose,
                                      song: songProp,
                                      song_id,
                                      external_song_id,
                                      default_part
                                  }: RequestContributionModalProps) => {
    const { t } = useI18n();
    const { showToast } = useToast();
    const [song, setSong] = useState<Song | null>(null);
    const [partType, setPartType] = useState('');
    const [section, setSection] = useState('');
    const [key, setKey] = useState('');
    const [note, setNote] = useState('');
    const [bountyXp, setBountyXp] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show) {
            if (songProp) setSong(songProp);
            if (default_part) setPartType(default_part.toLowerCase());
        }
    }, [show, songProp, default_part]);

    const handleSubmit = async () => {
        if (!song || !partType) {
            showToast(t('modals.requestContribution.toast.selectSongAndPartType'), { variant: 'danger' });
            return;
        }

        const payload: any = {
            part_type: partType,
            section,
        };
        if (key.trim()) payload.song_key = key;
        if (note.trim()) payload.note = note;
        if (bountyXp) payload.bounty_xp = parseInt(bountyXp, 10);

        if (song?.from_search || external_song_id) {
            payload.external_song_id = external_song_id || song?.uuid;
        } else {
            payload.song_id = song_id || song?.uuid;
        }

        if (song?.from_search) {
            payload.external_song_data = {
                ...song,
                from_search: true
            };
        }

        try {
            setLoading(true);
            await API.post('/community-contribution-requests', payload);
            showToast(t('modals.requestContribution.toast.submitted'), { variant: 'success' });
            onClose();
        } catch (e: any) {
            console.log("Failed to submit request", e);
            const errorMessage = e.response?.data?.message || t('modals.requestContribution.toast.submitFailed');
            showToast(errorMessage, { variant: 'danger' });
        } finally {
            setLoading(false);
            setSong(null);
            setPartType('');
            setSection('');
            setKey('');
            setNote('');
        }
    };

    return (
        <Transition appear show={show} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-150"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-150"
                            enterFrom="opacity-0 translate-y-2 scale-95"
                            enterTo="opacity-100 translate-y-0 scale-100"
                            leave="ease-in duration-100"
                            leaveFrom="opacity-100 translate-y-0 scale-100"
                            leaveTo="opacity-0 translate-y-2 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md rounded-[18px] border border-white/[0.06] bg-[#090909] p-4 text-left text-white shadow-2xl transition-all">
                                <Dialog.Title className="text-base font-semibold text-white">
                                    {t('modals.requestContribution.title')}
                                </Dialog.Title>
                                <Dialog.Description className="mt-1 text-xs text-white/55">
                                    {song ? (
                                        <div className="text-sm text-white/65">
                                            {t('modals.requestContribution.selectedSong', {title: song.title, artist: song.artist})}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-white/55">
                                            {t('modals.requestContribution.noSongSelected')}
                                        </div>
                                    )
                                    }
                                </Dialog.Description>

                                <div className="mt-4 space-y-3">
                                    {!song && (
                                        <SongSearchInput
                                            onSelect={(song) => setSong(song as any)}
                                            placeholder={t('modals.requestContribution.searchPlaceholder')}
                                            showPreview={true}
                                        />
                                    )}

                                    <select
                                        className="w-full rounded-[12px] border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-sm text-white focus:border-white/[0.18] focus:outline-none"
                                        value={partType}
                                        onChange={(e) => setPartType(e.target.value)}
                                    >
                                        <option value="">{t('modals.requestContribution.selectPart')}</option>
                                        {partTypes.map((pt) => (
                                            <option key={pt.value} value={pt.value}>{pt.label}</option>
                                        ))}
                                    </select>

                                    <input
                                        className="w-full rounded-[12px] border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-white/[0.18] focus:outline-none"
                                        placeholder={t('modals.requestContribution.section')}
                                        value={section}
                                        onChange={(e) => setSection(e.target.value)}
                                    />
                                    <input
                                        className="w-full rounded-[12px] border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-white/[0.18] focus:outline-none"
                                        placeholder={t('modals.requestContribution.songKeyOptional')}
                                        value={key}
                                        onChange={(e) => setKey(e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        className="w-full rounded-[12px] border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-white/[0.18] focus:outline-none"
                                        placeholder={t('modals.requestContribution.bountyXpOptional')}
                                        value={bountyXp}
                                        onChange={(e) => setBountyXp(e.target.value)}
                                    />
                                    <textarea
                                        rows={2}
                                        className="w-full rounded-[12px] border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-white/[0.18] focus:outline-none"
                                        placeholder={t('modals.requestContribution.additionalNotesOptional')}
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                    />
                                </div>

                                <div className="mt-5 flex items-center justify-end gap-2">
                                    <button
                                        onClick={onClose}
                                        className="rounded-full border border-white/[0.08] px-4 py-2 text-sm text-white/75 transition hover:border-white/[0.16] hover:text-white"
                                    >
                                        {t('modals.requestContribution.cancel')}
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className={`rounded-full px-4 py-2 text-sm font-medium text-white transition ${
                                            loading ? 'cursor-not-allowed bg-white/[0.08] text-white/35' : 'bg-ORANGE hover:brightness-110'
                                        }`}
                                    >
                                        {loading ? t('modals.requestContribution.submitting') : t('modals.requestContribution.submitRequest')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default RequestContributionModal;
